/* prepaidDisplay.js -- what the Vendor Dashboard SHOWS about prepaid contracts, read from the model's own rule.

   THE RULE HAS ONE OWNER: engines/prepaidTermination.js (activePrepaidContracts -- a vendor's End Month drops every
   linked contract that starts in or after it, never one that started at or before the close). This file does NOT
   restate it. It calls activePrepaidContracts and reports what it dropped, and it ties a contract to its vendor with
   the ONE identity resolver (engines/prepaidVendorMatch.js), the same calls prepaidForecastByVendor makes.

   TWO READERS (Brian, 2026-09-25):
     - the Prepaid tab greys a dropped contract and sums only the live set (prepaidDroppedContracts, called ONCE in
       VendorDashboard over the stored vendors);
     - the Vendor Modal summarises one prepaid vendor's contracts and its next payment (prepaidVendorSummary), run on
       the vendor set WITH THE MODAL'S UNSAVED End Month, so the line changes as the End Month is changed.
   Inert while no vendor carries an End Month: activePrepaidContracts returns the same array, nothing is dropped. */
import { activePrepaidContracts, prepaidEndMonth } from '../engines/prepaidTermination';
import { buildPrepaidVendorIndex, resolvePrepaidVendor } from '../engines/prepaidVendorMatch';
import { resolvePrepaidContractLine } from '../engines/prepaidRouting';

/* The contracts the model drops, each mapped to its vendor's End Month ('YYYY-MM').
     contracts : prepaid contract rows (the GET shape)
     vendors   : the vendor rows (Dashboard shape: id, name, isCat, endDate)
     opts      : { cutoff, lineOf, labelToLine } -- exactly what activePrepaidContracts takes
   Returns a Map keyed by the contract OBJECT (the same objects the caller holds); empty when nothing is dropped. */
export function prepaidDroppedContracts(contracts, vendors, opts) {
  const list = contracts || [];
  const o = opts || {};
  const live = activePrepaidContracts(list, vendors, o);
  const dropped = new Map();
  if (live === list || live.length === list.length) return dropped;
  const liveSet = new Set(live);
  const index = buildPrepaidVendorIndex(vendors || [], { lineOf: o.lineOf });
  const contractLine = c => resolvePrepaidContractLine(c, o.labelToLine || new Map()).lineKey;
  list.forEach(c => {
    if (liveSet.has(c)) return;
    const vendor = resolvePrepaidVendor(c, index, { lineOf: contractLine }).vendor;
    dropped.set(c, prepaidEndMonth(vendor));
  });
  return dropped;
}

/* One prepaid vendor's contracts, as the model books them.
     vendorId : the vendor whose contracts to summarise
     vendors  : the vendor set -- the caller substitutes the unsaved End Month on this vendor for a preview
   Returns { count, cancelled, lastMonth, nextPayment }:
     count       every stored contract linked to the vendor
     cancelled   how many of those the End Month drops
     lastMonth   the last month with amortisation among the kept contracts (null if none)
     nextPayment the first kept contract starting after the close (null if none) */
export function prepaidVendorSummary(vendorId, contracts, vendors, opts) {
  const list = contracts || [];
  const o = opts || {};
  const index = buildPrepaidVendorIndex(vendors || [], { lineOf: o.lineOf });
  const contractLine = c => resolvePrepaidContractLine(c, o.labelToLine || new Map()).lineKey;
  const linked = list.filter(c => {
    const v = resolvePrepaidVendor(c, index, { lineOf: contractLine }).vendor;
    return v != null && v.id === vendorId;
  });
  const dropped = prepaidDroppedContracts(list, vendors, o);
  let cancelled = 0, lastMonth = null, nextPayment = null;
  linked.forEach(c => {
    if (dropped.has(c)) { cancelled++; return; }
    Object.entries(c.amort || {}).forEach(([m, a]) => { if (a && (lastMonth == null || m > lastMonth)) lastMonth = m; });
    if (c.start && o.cutoff && c.start > o.cutoff && (nextPayment == null || c.start < nextPayment)) nextPayment = c.start;
  });
  return { count: linked.length, cancelled, lastMonth, nextPayment };
}
